import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Share2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { computeReview, monthTotal, prevMonth } from "./reviewStats";
import { MonthReviewCard } from "./MonthReviewCard";
import { YearReviewCard } from "./YearReviewCard";

const MODES = ["usd", "r", "hide"];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function ModePicker({ mode, setMode, t }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
      {MODES.map((m) => (
        <button key={m} type="button" data-testid={`year-review-mode-${m}`} onClick={() => setMode(m)}
          className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${mode === m ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
          {t(`insights.review.mode_${m}`)}
        </button>
      ))}
    </div>
  );
}

export default function YearReviewDialog({ open, onOpenChange, entries, years, year, setYear, months, period, setPeriod, month, setMonth }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [mode, setMode] = useState("usd");
  const [extra, setExtra] = useState({ badges: [], plans: [] });
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/achievements/me").catch(() => ({ data: [] })), api.get("/climb/plans").catch(() => ({ data: [] }))])
      .then(([a, p]) => setExtra({ badges: a.data || [], plans: p.data || [] }));
  }, [open]);

  const isMonth = period === "month";
  const key = isMonth ? month : String(year);
  const r = useMemo(() => computeReview(entries, { period: key, mode, ...extra }), [entries, key, mode, extra]);
  const change = useMemo(() => {
    if (!isMonth) return null;
    const prev = monthTotal(entries, prevMonth(month), mode);
    return prev && r.trades ? Math.round(((r.total - prev) / Math.abs(prev)) * 100) : null;
  }, [entries, isMonth, month, mode, r]);
  const lang = i18n.language || "en";
  const monthLabel = (ym) => cap(new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1)));

  const render = async () => {
    const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
    return { url, blob: await (await fetch(url)).blob() };
  };
  const run = async (share) => {
    setBusy(true);
    try {
      const { url, blob } = await render();
      const file = new File([blob], `${isMonth ? "month" : "year"}-in-review-${key}.png`, { type: "image/png" });
      if (share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: isMonth ? t("insights.review.monthCardTitle", { month: monthLabel(month) }) : t("insights.review.cardTitle", { year }) });
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = file.name; a.click();
        if (share) toast.info(t("insights.review.shareFallback"));
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(t("insights.review.exportError"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="year-review-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[620px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(isMonth ? "insights.review.monthTitle" : "insights.review.title")}</DialogTitle>
          <DialogDescription>{t(isMonth ? "insights.review.monthSubtitle" : "insights.review.subtitle")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {["year", "month"].map((p) => (
              <button key={p} type="button" data-testid={`review-period-${p}`} onClick={() => setPeriod(p)}
                className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${period === p ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                {t(`insights.review.period_${p}`)}
              </button>
            ))}
          </div>
          {isMonth ? (
            <select data-testid="month-review-month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold">
              {months.map((ym) => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
            </select>
          ) : (
            <select data-testid="year-review-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          <ModePicker mode={mode} setMode={setMode} t={t} />
        </div>
        {!r.trades && <p data-testid="year-review-empty" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{t(isMonth ? "insights.review.emptyMonth" : "insights.review.empty")}</p>}
        <div className="overflow-x-auto">
          {isMonth
            ? <MonthReviewCard ref={ref} r={r} ym={month} mode={mode} name={user?.name} change={change} t={t} lang={lang} />
            : <YearReviewCard ref={ref} r={r} year={year} mode={mode} name={user?.name} t={t} lang={lang} />}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="year-review-download" disabled={busy} onClick={() => run(false)} className="bg-blue-900 hover:bg-blue-800">
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}{t("insights.review.download")}
          </Button>
          <Button data-testid="year-review-share" disabled={busy} variant="outline" onClick={() => run(true)}>
            <Share2 size={14} className="mr-2" />{t("insights.review.share")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
